import React, { useState, useMemo, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Box,
  Card,
  CardContent,
  Typography,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  ListItemButton,
  IconButton,
  Chip,
  Stack,
  Divider,
  Paper,
  Avatar,
  Tooltip,
  LinearProgress,
  Badge,
  Button,
  Slide,
  Fade,
  useTheme,
  alpha,
  Grid,
} from '@mui/material';
import { RootState } from '../../store';
import {
  Close as CloseIcon,
  ArrowForward as ArrowForwardIcon,
  ArrowBack as ArrowBackIcon,
  QrCode as QrCodeIcon,
  Engineering as EngineeringIcon,
  Build as BuildIcon,
  Palette as CoatingIcon,
  CheckCircle as CompletedIcon,
  Schedule as InProgressIcon,
  AccessTime as PendingIcon,
  Person as PersonIcon,
  Timeline as TimelineIcon,
  LocalShipping as DispatchIcon,
  GridOn as GridIcon,
  Window as WindowIcon,
  OpenInNew as OpenableIcon,
  Business as BusinessIcon,
  LocationOn as LocationIcon,
  Phone as PhoneIcon,
  Email as EmailIcon,
} from '@mui/icons-material';
import { formatDate, formatCurrency, formatStatusText } from '../../utils/formatters';
import OrderTimeline from './OrderTimeline';
import { getProductDisplayName } from '../../utils/productUtils';
import {
  UnifiedTimelineProps,
  TimelineViewState,
  TimelineCustomer,
  TimelineSite,
  TimelineOrder,
  TimelineProduct,
  TimelineItem,
  TimelineEvent,
} from '../../types/timeline';
import { fetchOrderById, fetchOrders,selectOrderLoading } from '../../feature/orders/orderSlice';
import { ProductSection } from '@/feature/orders/OrderForm/ProductSection';
import { CircularProgress } from "@mui/material";
import DetailRow from '../../form/DetailRow';
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";

const UnifiedTimelineModal: React.FC<UnifiedTimelineProps> = ({
  isOpen,
  onClose,
  data,
//  sidePanelMode = true, // This prop is now ignored, using Redux state instead
}) => {
  const theme = useTheme();
  const dispatch = useDispatch();
  const timelineSettings = useSelector((state: RootState) => state.timeline);
  const sidePanelMode = timelineSettings.timelineModalMode === 'sidePanel';
  const orderLoading = useSelector(selectOrderLoading);
  const [showAllDetails, setShowAllDetails] = useState(false);
  // Fixed 20% width per panel
  const widthPercent = 20;
  
  // Debug logging
  console.log('UnifiedTimelineModal - Received data:', data);
  if (data?.type === 'customer') {
    console.log('Customer data:', data.customer);
    console.log('Customer sites:', data.customer?.sites);
    console.log('Sites length:', data.customer?.sites?.length);
  }
  const [currentView, setCurrentView] = useState<TimelineViewState>('sites');
  const [selectedSite, setSelectedSite] = useState<TimelineSite | null>(null);
  const [ordersList, setOrdersList] = useState<TimelineOrder[] | null>(null);
  const [productsList, setProductsList] = useState<TimelineProduct[] | null>(null);
  const [selectedOrder, setSelectedOrder] = useState<TimelineOrder | null>(null);
  const [orderDetails, setOrderDetails] = useState<any>(null);
  const [selectedProduct, setSelectedProduct] = useState<TimelineProduct | null>(null);
  const [selectedItem, setSelectedItem] = useState<TimelineItem | null>(null);

  const [sitesLoading, setSitesLoading] = useState(false);
  const [productsLoading, setProductsLoading] = useState(false);
  const [itemsLoading, setItemsLoading] = useState(false);
  const [timelineLoading, setTimelineLoading] = useState(false);

  useEffect(() => {
    if( data && data?.site?.id){
      setSelectedSite(data.site);
    }
  },[data]);



  useEffect(() => {
    if (selectedSite && selectedSite.id && selectedSite.customer?.id) {
      dispatch(fetchOrders({
        customerId: selectedSite.customer.id,
        siteId: selectedSite.id,
      })).then((result: any) => {
        // If using redux-thunk, result.payload should have the orders
        if (result?.payload?.orders) {
          console.log("result.payload.orders:", result.payload.orders);
          console.log("result.payload:", result.payload);
          console.log("result.payload.orders.products:", result.payload.orders.products);
          setOrdersList(result.payload.orders);
          setProductsList(result.payload.orders.products);
          
        }
      });
    }
    else if(selectedSite && selectedSite.id){
      dispatch(fetchOrders({
        siteId: selectedSite.id,
      })).then((result: any) => {
        // If using redux-thunk, result.payload should have the orders
        if (result?.payload?.orders) {
          setOrdersList(result.payload.orders);
          setProductsList(result.payload.orders.products);
        }
      });
    }
  }, [selectedSite, dispatch]);

  // useEffect(() => {
  //   console.log("selectedOrder changed:", selectedOrder?.id);
    
  //   if (selectedOrder && selectedOrder.id) {
  //     dispatch(fetchOrderById({
  //       orderId: selectedOrder?.id,
  //     })).then((result: any) => {
  //       // If using redux-thunk, result.payload should have the orders
  //       if (result?.payload?.orders) {
  //         setSelectedProduct(result.payload);
  //       }
  //     });
  //   }
  // }, [selectedOrder, dispatch]);

  useEffect(() => {
    if (selectedOrder && selectedOrder.id) {
      console.log("selectedOrder changed:", selectedOrder?.id);
      setSitesLoading(true);
      setProductsLoading(true);
      setTimelineLoading(true);
      setItemsLoading(true);
      dispatch(fetchOrderById(selectedOrder.id)).then((result: any) => {
        if (result?.payload) {
          setOrderDetails(result.payload);
        }
        setSitesLoading(false);
        setProductsLoading(false);
        setTimelineLoading(false);
        setItemsLoading(false);
      });
    }
  }, [selectedOrder, dispatch]);

  useEffect(()=>{
    console.log("selected Product changed:", selectedProduct);
    console.log("data====>", data);
  },[selectedProduct,data])

  

  // Get all items from all products in an order
  const getAllItems = (order: TimelineOrder): TimelineItem[] => {
    const items: TimelineItem[] = [];
    order.products?.forEach(product => {
      if (product.frame) items.push(...product.frame);
      if (product.panel) items.push(...product.panel);
      if (product.panelWindow) items.push(...product.panelWindow);
      if (product.net) items.push(...product.net);
      if (product.openableType) items.push(...product.openableType);
      if (product.items) items.push(...product.items);
    });
    return items;
  };

  // Get items for selected product
  const getProductItems = (product: TimelineProduct): TimelineItem[] => {
    const items: TimelineItem[] = [];
    if (product.frame) items.push(...product.frame);
    if (product.panel) items.push(...product.panel);
    if (product.panelWindow) items.push(...product.panelWindow);
    if (product.net) items.push(...product.net);
    if (product.openableType) items.push(...product.openableType);
    // if (product.items) items.push(...product.items);
    return items;
  };

  // Convert item logs to timeline events
  const getTimelineEvents = (item: TimelineItem): TimelineEvent[] => {
    if (!item.logs || item.logs.length === 0) {
      // If no logs, create a basic timeline based on current stage
      const stages = getStagesForItemType(item.type);
      return stages.map((stage, index) => {
        let status: 'completed' | 'in_progress' | 'pending' | 'skipped' = 'pending';
        
        // Find the current stage index in the stages array
        const currentStageIndex = stages.indexOf(item.currentStage || '');
        
        // Determine status based on sequential progression
        if (index < currentStageIndex) {
          // All stages before the current stage are completed
          status = 'completed';
        } else if (index === currentStageIndex) {
          // Current stage takes the item's status
          status = item.status as any || 'in_progress';
        } else {
          // All stages after the current stage are pending
          status = 'pending';
        }

        return {
          id: `stage-${index}`,
          stage: stage,
          status: status,
          user_id: item.assignedTo || 'system',
          user_name: item.assignedTo || 'System',
          timestamp: item.createdAt ? new Date(item.createdAt).toISOString() : new Date().toISOString(),
          notes: item.notes || '',
          duration_hours: null,
          qr_code: item.qrCode,
          stage_order: index,
          previous_stage: index > 0 ? stages[index - 1] : undefined,
          next_stage: index < stages.length - 1 ? stages[index + 1] : undefined,
        };
      });
    }
    
    return item.logs.map((log, index) => ({
      id: log.id,
      stage: log.stage,
      status: log.status as 'completed' | 'in_progress' | 'pending' | 'skipped',
      user_id: log.user_id,
      user_name: log.user_name,
      timestamp: log.timestamp,
      notes: log.notes,
      duration_hours: log.duration_hours || null,
      qr_code: item.qrCode,
      stage_order: index,
      previous_stage: log.previous_stage,
      next_stage: log.next_stage,
    }));
  };

  // Get stages for item type
  const getStagesForItemType = (itemType: string) => {
    const stageMap: Record<string, string[]> = {
      'Frame': ['Fabrication', 'Coating', 'Dispatch'],
      'Panel': [
        'Cutting', 'Assembly', 'Hinges', 'Member', 'Bar', 'Full Welding',
        'Grinding', 'Leveling', 'Finishing', 'Khacha', 'Coating',
        'Accessories', 'Glass Fitting', 'Dispatch'
      ],
      'Panel Window': ['Fabrication', 'Coating', 'Fitting in Panel'],
      'Net Fixing': ['Frame Cut', 'Weld', 'Net Fixing'],
      'Openable Type': ['Design', 'Fabrication', 'Assembly', 'Coating', 'Dispatch'],
    };
    return stageMap[itemType] || ['Fabrication', 'Assembly', 'Dispatch'];
  };

  const getItemTypeIcon = (type: string) => {
    const typeLower = type.toLowerCase();
    if (typeLower.includes('frame')) {
      return <EngineeringIcon />;
    } else if (typeLower.includes('panel') && typeLower.includes('window')) {
      return <WindowIcon />;
    } else if (typeLower.includes('panel')) {
      return <GridIcon />;
    } else if (typeLower.includes('net')) {
      return <GridIcon />;
    } else if (typeLower.includes('openable')) {
      return <OpenableIcon />;
    } else if (typeLower.includes('door')) {
      return <BuildIcon />;
    } else if (typeLower.includes('window')) {
      return <WindowIcon />;
    }
    return <BuildIcon />;
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'completed':
        return 'success';
      case 'in_progress':
        return 'primary';
      case 'pending':
        return 'warning';
      case 'dispatched':
        return 'secondary';
      default:
        return 'default';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'completed':
        return <CompletedIcon />;
      case 'in_progress':
        return <InProgressIcon />;
      case 'pending':
        return <PendingIcon />;
      case 'dispatched':
        return <DispatchIcon />;
      default:
        return <PendingIcon />;
    }
  };

  // Navigation handlers
  const handleSiteSelect = (site: TimelineSite) => {
    setSelectedSite(site);
    setCurrentView('orders');
  };

  const handleOrderSelect = (order: TimelineOrder) => {
    setSelectedOrder(order);
    setCurrentView('products');
  };

  const handleProductSelect = (product: TimelineProduct) => {
    setSelectedProduct(product);
    setCurrentView('items');
  };

  const handleItemSelect = (item: TimelineItem) => {
    setSelectedItem(item);
    setCurrentView('timeline');
  };

  const handleBack = () => {
    if (currentView === 'timeline') {
      setCurrentView('items');

      console.log('currentView === timeline back to item :', currentView)
    } else if (currentView === 'items') {
      setCurrentView('products');
      console.log('currentView === items back to product :', currentView)
    } else if (currentView === 'products') {
      setCurrentView('orders');
      console.log('currentView === product back to order :', currentView)
    } else if (currentView === 'orders') {
      setCurrentView('sites');
      console.log('currentView === order back to site :', currentView)
    }
  };

  // Render Sites View (for customer details)
  const renderSitesView = () => {

    // const [showAllDetails, setShowAllDetails] = useState(false);

    if (sitesLoading) {
      return (
        <Box sx={{ textAlign: 'center', py: 4 }}>
          <CircularProgress />
          <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
            Loading sites...
          </Typography>
        </Box>
      );
    }

    if (data.type !== 'customer' || !data.customer?.sites) return null;

    return (
      <Box sx={{ 
        height: '100%', 
        display: 'flex', 
        flexDirection: 'column',
        bgcolor: 'background.paper',
        borderLeft: sidePanelMode ? 'none' : 1,
        borderColor: 'divider',
        boxShadow: sidePanelMode ? 'none' : 2,
      }}>
        {/* Header */}
        <Box sx={{borderBottom: 1, borderColor: 'divider', pt:3, pl:3,pr:3, pb:1}}>
          <Box sx={{display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Box sx={{ display: 'flex', }}>
              <Typography variant="h5" fontWeight={600}>
                {/* Sites ({data.customer.sites.length}) */}
                {data.customer?.name}
              </Typography>
              <IconButton size="small" onClick={() => setShowAllDetails((prev) => !prev)}>
                {showAllDetails ? <ExpandLessIcon /> : <ExpandMoreIcon />}
              </IconButton>
              
            </Box>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              Select a site to view its orders
              {sidePanelMode && (
                <IconButton onClick={onClose} size="small">
                  <CloseIcon />
                </IconButton>
              )}
            </Typography>
          </Box>

          <Grid 
              container 
              spacing={2} 
              sx={{ mt: 1 }}
            >
              {showAllDetails && (
               <>
              
              <Grid item xs={12} sm={6}>
                <DetailRow label="Name" value={data.customer?.name} />
              </Grid>
              <Grid item xs={12} sm={6}>
                <DetailRow label="Phone" value={data.customer?.phone} />
              </Grid>

              <Grid item xs={12} sm={6}>
                <DetailRow label="Email" value={data.customer?.email} />
              </Grid>              
              <Grid item xs={12} sm={6}>
                <DetailRow 
                  label="Address" 
                  value={`${data.customer?.city || ""}, ${data.customer?.state || ""}`} 
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <DetailRow label="Contact Person" value={data.customer?.contactPerson} />
              </Grid>
              <Grid item xs={12} sm={6}>
                <DetailRow label="Contact Phone" value={data.customer?.contactPhone} />
              </Grid>

              <Grid item xs={12} sm={6}>
                <DetailRow label="Contact Email" value={data.customer?.contactEmail} />
              </Grid>
              </>
            )}
            </Grid>

        </Box>

        {/* Sites List */}
        <Box sx={{ flex: 1, overflow: 'auto', p: 2, }}>

          {/* <Card
            sx={{
              mb:2,
              // p:2,
              pt:2,pl:2,pr:2,
              // pb:-2,
              width: '100%',
              cursor: 'pointer',
              transition: 'all 0.2s ease-in-out',
              '&:hover': {
                transform: 'translateY(-2px)',
                boxShadow: theme.shadows[4],
              },
            }}
            
          >             */}
            {/* <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <DetailRow label="Name" value={data.customer?.name} />
              </Grid>
              <Grid item xs={12} sm={6}>
                <DetailRow label="Phone" value={data.customer?.phone} />
              </Grid>
              <Grid item xs={12} sm={6}>
                <DetailRow label="Email" value={data.customer?.email} />
              </Grid>

              {showAllDetails && (
                <>
                  {data.customer?.type && (
                    <Grid item xs={12} sm={6}>
                      <DetailRow label="Type" value={data.customer?.type} />
                    </Grid>
                  )}
                  <Grid item xs={12} sm={6}>
                    <DetailRow
                      label="Address"
                      value={`${data.customer?.city || ""}, ${data.customer?.state || ""}`}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <DetailRow label="Contact Person" value={data.customer?.contactPerson} />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <DetailRow label="Contact Phone" value={data.customer?.contactPhone} />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <DetailRow label="Contact Email" value={data.customer?.contactEmail} />
                  </Grid>
                </>
              )}
            </Grid> */}

            {/* <Grid 
              container 
              spacing={2} 
              sx={{ mt: 1 }}
            >
              
              <Grid item xs={12} sm={6}>
                <DetailRow label="Name" value={data.customer?.name} />
              </Grid>
              <Grid item xs={12} sm={6}>
                <DetailRow label="Phone" value={data.customer?.phone} />
              </Grid>

              <Grid item xs={12} sm={6}>
                <DetailRow label="Email" value={data.customer?.email} />
              </Grid>
              {showAllDetails && (
               <>
              <Grid item xs={12} sm={6}>
                <DetailRow 
                  label="Address" 
                  value={`${data.customer?.city || ""}, ${data.customer?.state || ""}`} 
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <DetailRow label="Contact Person" value={data.customer?.contactPerson} />
              </Grid>
              <Grid item xs={12} sm={6}>
                <DetailRow label="Contact Phone" value={data.customer?.contactPhone} />
              </Grid>

              <Grid item xs={12} sm={6}>
                <DetailRow label="Contact Email" value={data.customer?.contactEmail} />
              </Grid>
              </>
            )}
            </Grid> */}
            
            {/* <Box sx={{textAlign: 'right' }}>
              <Button
                variant="text"
                size="small"
                onClick={() => setShowAllDetails((prev) => !prev)}
              >
                {showAllDetails ? 'Hide Details' : 'View All Details'}
              </Button>
            </Box>
          </Card> */}

          {data.customer.sites.length === 0 ? (
            <Box sx={{ textAlign: 'center', py: 4 }}>
              <Typography variant="h6" color="text.secondary">
                No sites found
              </Typography>
              <Typography variant="body2" color="text.secondary">
                This customer doesn't have any sites yet.
              </Typography>
            </Box>
          ) : (
            <List sx={{ p: 0 }}>
              {data.customer.sites.map((site, index) => (
                <ListItem key={site.id} sx={{ p: 0, mb: 2 }}>
                  <Card
                    sx={{
                      width: '100%',
                      cursor: 'pointer',
                      transition: 'all 0.2s ease-in-out',
                      '&:hover': {
                        transform: 'translateY(-2px)',
                        boxShadow: theme.shadows[4],
                      },
                    }}
                    onClick={() => handleSiteSelect(site)}
                  >
                    <CardContent>
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                          <Avatar sx={{ bgcolor: 'primary.main' }}>
                            <LocationIcon />
                          </Avatar>
                          <Box>
                            <Typography variant="h6" fontWeight={600}>
                              {site.name}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              {site.address ? `${site.address}, ${site.city || ''}, ${site.state || ''}` : 'No address'}
                            </Typography>
                          </Box>
                        </Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Chip
                            label={site.status || 'active'}
                            color={site.status === 'active' ? 'success' : 'default'}
                            size="small"
                          />
                          <ArrowForwardIcon color="action" />
                        </Box>
                      </Box>
                      
                      {/* Site Details */}
                      <Box sx={{ mt: 2, display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                        {site.contactPerson && (
                          <Chip label={`Contact: ${site.contactPerson}`} size="small" variant="outlined" />
                        )}
                        {site.contactPhone && (
                          <Chip label={`Phone: ${site.contactPhone}`} size="small" variant="outlined" />
                        )}
                        {site.orders && (
                          <Chip label={`Orders: ${site.orders.length}`} size="small" variant="outlined" />
                        )}
                      </Box>
                    </CardContent>
                  </Card>
                </ListItem>
              ))}
            </List>
          )}
        </Box>
      </Box>
    );
  };

  // Render Orders View (for site details or after site selection)
  const renderOrdersView = () => {
    const orders = ordersList;
    // const orders = data.type === 'site' ? data.site?.orders : selectedSite?.orders;
    console.log('Rendering Orders View - Orders->orders:', orders);
    console.log('Rendering Orders View - Orders->data:', data);
    console.log('Rendering Orders View - Orders->selectedsite:', selectedSite);
    if (!orders) return null;
    // const [showAllDetails, setShowAllDetails] = useState(false);

    return (
      <Box sx={{ 
        height: '100%', 
        display: 'flex', 
        flexDirection: 'column',
        bgcolor: 'background.paper',
        borderLeft: sidePanelMode ? 'none' : 1,
        borderColor: 'divider',
        boxShadow: sidePanelMode ? 'none' : 2,
      }}>
        {/* Header */}
        <Box sx={{ borderBottom: 1, borderColor: 'divider', pt:3, pl:3,pr:3, pb:1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center',justifyContent: 'space-between' }}>
            
            <Box sx={{ display: 'flex', }}>
              <Typography variant="h5" fontWeight={600}>
                {/* Orders ({orders.length}) */}
                {data.type === 'site' ? data.site?.name : selectedSite?.name}
              </Typography>
              <IconButton size="small" onClick={() => setShowAllDetails((prev) => !prev)}>
                {showAllDetails ? <ExpandLessIcon /> : <ExpandMoreIcon />}
              </IconButton>              
            </Box>

            <Typography variant="body2" color="text.secondary">
                Select an order to view details
                {/* {data.type === 'site' ? data.site?.name : selectedSite?.name} • Select an order to view details */}
            </Typography>
          </Box>

          <Grid 
              container 
              spacing={2} 
              sx={{ mt: 1 }}
            >
              {showAllDetails && (
              <>              
                <Grid item xs={12} sm={6}>
                  <DetailRow label="Address" value={data.site?.address} />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <DetailRow label="City" value={data.site?.city} />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <DetailRow label="Contact Person" value={data.site?.contactPerson} />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <DetailRow label="Contact Phone" value={data.site?.contactPhone} />
                </Grid>
              </>
            )}
            </Grid>


        </Box>

        {/* Orders List */}
        <Box sx={{ flex: 1, overflow: 'auto', p: 2 }}>
          {orderLoading && (!orders || orders.length === 0) ? (
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <CircularProgress />
            <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
              Loading orders...
            </Typography>
          </Box>
        ):orders.length === 0 ? (
            <Box sx={{ textAlign: 'center', py: 4 }}>
              <Typography variant="h6" color="text.secondary">
                No orders found
              </Typography>
              <Typography variant="body2" color="text.secondary">
                This site doesn't have any orders yet.
              </Typography>
            </Box>
          ) : (
            <List sx={{ p: 0 }}>
              {orders.map((order, index) => (
                <ListItem key={order.id} sx={{ p: 0, mb: 2 }}>
                  <Card
                    sx={{
                      width: '100%',
                      cursor: 'pointer',
                      transition: 'all 0.2s ease-in-out',
                      '&:hover': {
                        transform: 'translateY(-2px)',
                        boxShadow: theme.shadows[4],
                      },
                    }}
                    onClick={() => handleOrderSelect(order)}
                  >
                    <CardContent>
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                          <Avatar sx={{ bgcolor: 'secondary.main' }}>
                            {index + 1}
                          </Avatar>
                          <Box>
                            <Typography variant="h6" fontWeight={600}>
                              {order.orderNumberNew || order.orderNumber || order.order_tracking_code || `Order ${order.id.slice(-8)}`}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              {order.customer?.name} • {formatDate(order.createdAt)}
                            </Typography>
                          </Box>
                        </Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Chip
                            label={formatStatusText(order.status)}
                            color={getStatusColor(order.status) as any}
                            size="small"
                            icon={getStatusIcon(order.status)}
                          />
                          <ArrowForwardIcon color="action" />
                        </Box>
                      </Box>
                      
                      {/* Order Details */}
                      <Box sx={{ mt: 2, display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                        {order.priority && (
                          <Chip label={`Priority: ${order.priority}`} size="small" variant="outlined" />
                        )}
                        {order.paymentStatus && (
                          <Chip label={`Payment: ${order.paymentStatus}`} size="small" variant="outlined" />
                        )}
                        {order.totalAmount && (
                          <Chip label={`Amount: ${formatCurrency(order.totalAmount)}`} size="small" variant="outlined" />
                        )}
                        {order.products && (
                          <Chip label={`Products: ${order.products.length}`} size="small" variant="outlined" />
                        )}
                      </Box>
                    </CardContent>
                  </Card>
                </ListItem>
              ))}
            </List>
          )}
        </Box>
      </Box>
    );
  };

  // Render Products View (for order details)
  const renderProductsView = () => {

    if (productsLoading) {
      return (
        <Box sx={{ textAlign: 'center', py: 4 }}>
          <CircularProgress />
          <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
            Loading Products...
          </Typography>
        </Box>
      );
    }

    const products = orderDetails?.products;
    // const products = data.type === 'order' ? data.order?.products : selectedOrder?.products;
    console.log('products',products)
    console.log('orderDetails',orderDetails?.products)
    if (!products) return null;

    return (
      <Box sx={{ 
        height: '100%', 
        display: 'flex', 
        flexDirection: 'column',
        bgcolor: 'background.paper',
        borderLeft: sidePanelMode ? 'none' : 1,
        borderColor: 'divider',
        boxShadow: sidePanelMode ? 'none' : 2,
      }}>
        {/* Header */}
        <Box sx={{borderBottom: 1, borderColor: 'divider',pt:3, pl:3,pr:3, pb:1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <IconButton onClick={handleBack} size="small">
              <ArrowBackIcon />
            </IconButton>
            <Box>
              <Typography variant="h5" fontWeight={600}>
                Set(s) ({products.length})
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {data.type === 'order' ? data.order?.orderNumber || data.order?.orderNumberNew : selectedOrder?.orderNumber || selectedOrder?.orderNumberNew} • Select a set to view items
              </Typography>
            </Box>
          </Box>
        </Box>

        {/* Products List */}
        <Box sx={{ flex: 1, overflow: 'auto', p: 2 }}>
          {products.length === 0 ? (
            <Box sx={{ textAlign: 'center', py: 4 }}>
              <Typography variant="h6" color="text.secondary">
                No products found
              </Typography>
              <Typography variant="body2" color="text.secondary">
                This order doesn't have any products yet.
              </Typography>
            </Box>
          ) : (
            <List sx={{ p: 0 }}>
              {products.map((product, index) => {
                const productItems = getProductItems(product);
                const completedItems = productItems.filter(item => item.status === 'completed').length;
                const progress = productItems.length > 0 ? (completedItems / productItems.length) * 100 : 0;

                return (
                  <ListItem key={product.id} sx={{ p: 0, mb: 2 }}>
                    <Card
                      sx={{
                        width: '100%',
                        cursor: 'pointer',
                        transition: 'all 0.2s ease-in-out',
                        '&:hover': {
                          transform: 'translateY(-2px)',
                          boxShadow: theme.shadows[4],
                        },
                      }}
                      onClick={() => handleProductSelect(product)}
                    >
                      <CardContent>
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                            <Avatar sx={{ bgcolor: 'primary.main' }}>
                              {index + 1}
                            </Avatar>
                            <Box>
                              <Typography variant="h6" fontWeight={600}>
                                {product.name}
                              </Typography>
                              <Typography variant="body2" color="text.secondary">
                                {product.type?.toUpperCase()} • {productItems.length} part(s)
                              </Typography>
                            </Box>
                          </Box>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Chip
                              label={formatStatusText(product.status || 'pending')}
                              color={getStatusColor(product.status || 'pending') as any}
                              size="small"
                              icon={getStatusIcon(product.status || 'pending')}
                            />
                            <ArrowForwardIcon color="action" />
                          </Box>
                        </Box>
                        
                        {/* Product Details */}
                        <Box sx={{ mt: 2, display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                          {product.label && (
                            <Chip label={`Label: ${product.label}`} size="small" variant="outlined" />
                          )}
                          {product.location && (
                            <Chip label={`Location: ${product.location}`} size="small" variant="outlined" />
                          )}
                          {product.evaOrder && (
                            <Chip label={`EVA: ${product.evaOrder}`} size="small" variant="outlined" />
                          )}
                        </Box>

                        {/* Progress Bar */}
                        <Box sx={{ mt: 2 }}>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                            <Typography variant="body2" color="text.secondary">
                              Progress
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              {completedItems} / {productItems.length}
                            </Typography>
                          </Box>
                          <LinearProgress
                            variant="determinate"
                            value={progress}
                            sx={{ height: 6, borderRadius: 3 }}
                          />
                        </Box>
                      </CardContent>
                    </Card>
                  </ListItem>
                );
              })}
            </List>
          )}
        </Box>
      </Box>
    );
  };

  // Render Items View (for product details)
  const renderItemsView = () => {

    if (itemsLoading) {
      return (
        <Box sx={{ textAlign: 'center', py: 4 }}>
          <CircularProgress />
          <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
            Loading Items...
          </Typography>
        </Box>
      );
    }

    if (!selectedProduct) return null;
    const productItems = getProductItems(selectedProduct);

    return (
      <Box sx={{ 
        height: '100%', 
        display: 'flex', 
        flexDirection: 'column',
        bgcolor: 'background.paper',
        borderLeft: sidePanelMode ? 'none' : 1,
        borderColor: 'divider',
        boxShadow: sidePanelMode ? 'none' : 2,
      }}>
        {/* Header */}
        <Box sx={{borderBottom: 1, borderColor: 'divider',pt:3, pl:3,pr:3, pb:1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <IconButton onClick={handleBack} size="small">
              <ArrowBackIcon />
            </IconButton>
            <Box>
              <Typography variant="h5" fontWeight={600}>
                {selectedProduct.name} - Part(s)
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {selectedProduct.type} • {productItems.length} Part(s) • Select an part to view timeline
              </Typography>
            </Box>
          </Box>
        </Box>

        {/* Items List */}
        <Box sx={{ flex: 1, overflow: 'auto', p: 2 }}>
          {productItems.length === 0 ? (
            <Box sx={{ textAlign: 'center', py: 4 }}>
              <Typography variant="h6" color="text.secondary">
                No items found
              </Typography>
              <Typography variant="body2" color="text.secondary">
                This product doesn't have any items yet.
              </Typography>
            </Box>
          ) : (
            <List sx={{ p: 0 }}>
              {productItems.map((item, index) => (
                <ListItem key={item.id} sx={{ p: 0, mb: 2 }}>
                  <Card
                    sx={{
                      width: '100%',
                      cursor: 'pointer',
                      transition: 'all 0.2s ease-in-out',
                      '&:hover': {
                        transform: 'translateY(-2px)',
                        boxShadow: theme.shadows[4],
                      },
                    }}
                    onClick={() => handleItemSelect(item)}
                  >
                    <CardContent>
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                          <Avatar sx={{ bgcolor: 'secondary.main' }}>
                            {getItemTypeIcon(item.type)}
                          </Avatar>
                          <Box>
                            <Typography variant="h6" fontWeight={600}>
                              {item.itemNumber || `Item ${item.id.slice(-4)}`}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              {item.type} • Stage: {item.currentStage || 'Not Started'}
                            </Typography>
                          </Box>
                        </Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Chip
                            label={formatStatusText(item.status)}
                            color={getStatusColor(item.status) as any}
                            size="small"
                            icon={getStatusIcon(item.status)}
                          />
                          <ArrowForwardIcon color="action" />
                        </Box>
                      </Box>
                      
                      {/* Item Details */}
                      <Box sx={{ mt: 2, display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                        {item.width && item.height && (
                          <Chip label={`Dimensions: ${item.width} x ${item.height}`} size="small" variant="outlined" />
                        )}
                        {item.material && (
                          <Chip label={`Material: ${item.material}`} size="small" variant="outlined" />
                        )}
                        {item.assignedTo && (
                          <Chip label={`Assigned: ${item.assignedTo}`} size="small" variant="outlined" />
                        )}
                      </Box>

                      {/* QR Code */}
                      {item.qrCode && (
                        <Box sx={{ mt: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                          <QrCodeIcon fontSize="small" color="action" />
                          <Typography variant="caption" color="text.secondary">
                            QR: {item.qrCode}
                          </Typography>
                        </Box>
                      )}
                    </CardContent>
                  </Card>
                </ListItem>
              ))}
            </List>
          )}
        </Box>
      </Box>
    );
  };

  // Render Timeline View (for item details)
  const renderTimelineView = () => {
    if (!selectedItem) return null;

    return (
      <Box sx={{ 
        height: '100%', 
        display: 'flex', 
        flexDirection: 'column',
        bgcolor: 'background.paper',
        borderLeft: sidePanelMode ? 'none' : 1,
        borderColor: 'divider',
        boxShadow: sidePanelMode ? 'none' : 2,
      }}>
        {/* Header */}
        <Box sx={{ borderBottom: 1, borderColor: 'divider', pt:3, pl:3,pr:3, pb:1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <IconButton onClick={handleBack} size="small">
              <ArrowBackIcon />
            </IconButton>
            <Box>
              <Typography variant="h5" fontWeight={600}>
                {selectedItem.itemNumber || `Item ${selectedItem.id.slice(-4)}`}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {selectedItem.type} • {selectedItem.currentStage || 'Not Started'}
              </Typography>
            </Box>
          </Box>
        </Box>

        {/* Timeline */}
        <Box sx={{ flex: 1, overflow: 'hidden' }}>
          <OrderTimeline
            orderId={selectedOrder?.id || data.order?.id || ''}
            orderNumber={selectedOrder?.orderNumber || selectedOrder?.orderNumberNew || data.order?.orderNumber || data.order?.orderNumberNew || 'Unknown'}
            itemId={selectedItem.id}
            itemType={selectedItem.type}
            events={getTimelineEvents(selectedItem)}
            onClose={onClose}
          />
        </Box>
      </Box>
    );
  };

  if (!isOpen) return null;

  // Determine initial view based on data type
  const getInitialView = (): TimelineViewState => {
    if (data.type === 'customer') return 'sites';
    if (data.type === 'site') return 'orders';
    if (data.type === 'order') return 'products';
    return 'sites';
  };

  // Set initial view if not already set
  React.useEffect(() => {
    if (isOpen && currentView === 'sites') {
      setCurrentView(getInitialView());
    }
  }, [isOpen, data.type]);

  // Side Panel Mode
  if (sidePanelMode) {
    return (
      <>
        {/* Backdrop */}
        <Box
          sx={{
            position: 'fixed',
            top: 0,
            left: 0,
            width: '100vw',
            height: '100vh',
            bgcolor: alpha(theme.palette.common.black, 0.5),
            zIndex: 1299,
          }}
          onClick={onClose}
        />
        
        {/* Side Panel */}
        <Slide direction="left" in={isOpen} mountOnEnter unmountOnExit>
          <Box
            sx={{
              position: 'fixed',
              top: 0,
              right: 0,
              width: { xs: '100vw', sm: '20vw' },
              height: '100vh',
              zIndex: 1300,
              bgcolor: 'background.paper',
              display: 'flex',
              flexDirection: 'column',
              boxShadow: theme.shadows[8],
              borderLeft: 1,
              borderColor: 'divider',
            }}
          >
            {currentView === 'sites' && renderSitesView()}
            {currentView === 'orders' && renderOrdersView()}
            {currentView === 'products' && renderProductsView()}
            {currentView === 'items' && renderItemsView()}
            {currentView === 'timeline' && renderTimelineView()}
          </Box>
        </Slide>
      </>
    );
  }

  // Full Screen Mode with Cascading Panels (similar to existing OrderDetailsModal)
  return (
    <Box
      sx={{
        position: 'fixed',
        top: 0,
        left: 0,
        width: '100vw',
        height: '100vh',
        zIndex: 1300,
        bgcolor: 'transparent',
        overflow: 'hidden',
        pointerEvents: 'none',
      }}
    >
      {/* Close Button for Full Screen Mode */}
      <IconButton
        onClick={onClose}
        sx={{
          position: 'absolute',
          top: 16,
          right: 16,
          zIndex: 1400,
          bgcolor: 'background.paper',
          boxShadow: 2,
          pointerEvents: 'auto',
          '&:hover': {
            bgcolor: 'action.hover',
          },
        }}
      >
        <CloseIcon />
      </IconButton>
      
      {/* Render appropriate view based on current state */}
      {currentView === 'sites' && (
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            right: 0,
            width: `${widthPercent}%`,
            height: '100vh',
            zIndex: 1000,
            pointerEvents: 'auto',
          }}
        >
          {renderSitesView()}
        </Box>
      )}

      {currentView === 'orders' && (
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            right: 0,
            width: `${widthPercent}%`,
            height: '100vh',
            zIndex: 1000,
            pointerEvents: 'auto',
          }}
        >
          {renderOrdersView()}
        </Box>
      )}

      {currentView === 'products' && (
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            right: 0,
            width: `${widthPercent}%`,
            height: '100vh',
            zIndex: 1000,
            pointerEvents: 'auto',
          }}
        >
          {renderProductsView()}
        </Box>
      )}

      {currentView === 'items' && (
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            right: 0,
            width: `${widthPercent}%`,
            height: '100vh',
            zIndex: 1000,
            pointerEvents: 'auto',
          }}
        >
          {renderItemsView()}
        </Box>
      )}

      {currentView === 'timeline' && (
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            right: 0,
            width: `${widthPercent}%`,
            height: '100vh',
            zIndex: 1000,
            pointerEvents: 'auto',
          }}
        >
          {renderTimelineView()}
        </Box>
      )}
    </Box>
  );
};

export default UnifiedTimelineModal;
